import { useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { isMobile } from '@deficonnect/utils'
import { Text, Flex, Box, Grid, Link, Img } from 'vvs-uikit'
import { useGetMoleQuery } from 'vvs-gotchi-graphql'
import { CloseButton } from 'components/CloseButton'
import { useTranslation } from 'contexts/Localization'
import { formatNumber } from 'utils/formatBalance'
import { Tier, moleFromGraphqlData } from 'state/vvsgotchi/models'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import HealthIconImage from 'views/VvsGotchi/components/HealthIconImage'
import ScreenFooter from 'views/VvsGotchi/components/ScreenFooter'
import ActionButton from 'views/VvsGotchi/components/ActionButton'
import type { RarityTierDetails } from 'views/VvsGotchi/Quest/types'
import { useTheme } from 'styled-components'
import { Path } from 'config/constants/paths'
import withRankSuffix from 'utils/rankSuffix'
import { getVVSMinerMoleMintedUrl } from 'utils/mintedNftCollectionUrls'
import historyBackOrReplace from 'utils/historyBackOrReplace'
import parseMoleTokenId from 'views/VvsGotchi/utils/parseMoleTokenId'

import NameYourMole from './NameYourMole'
import RemoveFromTeamBtn from './RemoveFromTeamBtn'

import { rarityToName, tierToIcon, tierToIconSize, tierToName, rarityTierMatrix } from '../config'

import { MoleProgressBar, StyledTeamEditContainer, StyledTeamMoleProfileWrapper, GridWideCell } from './styles'
import { DarkPageContainer } from '../styles'
import { MoleAvatar } from '../components/styles'

const MoleProfile = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { tokenId: tokenIdParam } = useParams<{ tokenId: string }>()
  const tokenId = parseMoleTokenId(tokenIdParam)
  const history = useHistory()
  const { data: moleData, loading } = useGetMoleQuery({
    variables: { tokenId },
    skip: tokenId === null,
  })
  const { team, owner, loading: ownerLoading } = useGotchiOwner()

  const [isInOwnerTeam, isOwning, mole] = useMemo(() => {
    const owningMole = team?.find((m) => m.tokenId === tokenId)
    if (owningMole) {
      return [true, true, owningMole]
    }

    return [
      false,
      owner?.moles.some((m) => m.tokenId === tokenId),
      moleData?.mole ? moleFromGraphqlData(moleData.mole) : null,
    ]
  }, [tokenId, owner, team, moleData?.mole])

  const moleTierIconSrc = tierToIcon[mole?.tier]
  const tierName = tierToName[mole?.tier]
  const rarity = rarityToName[mole?.rarity]
  const rarityTierDetails = rarityTierMatrix[mole?.rarity]

  if (!mole) {
    return (
      <DarkPageContainer>
        <Flex justifyContent="end" p={3}>
          <CloseButton fill="white" onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)} />
        </Flex>
        <Flex flex="1" justifyContent="center" alignItems="center">
          <Text color="white">{loading || ownerLoading ? t('Loading...') : t('Mole not found')}</Text>
        </Flex>
      </DarkPageContainer>
    )
  }

  return (
    <DarkPageContainer>
      <StyledTeamMoleProfileWrapper>
        <Flex justifyContent="end" mb="8px">
          <CloseButton fill="white" onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)} />
        </Flex>
        <Grid gridTemplateColumns="1fr 2fr">
          <GridWideCell>
            <Flex>
              <MoleAvatar size="105px" rounded image={mole?.imageUrl} />
            </Flex>
          </GridWideCell>
          <Flex flexDirection="column">
            <Text color="white" mt="-5px">
              {mole?.name}
            </Text>

            {isInOwnerTeam && <NameYourMole mole={mole} />}
          </Flex>
          <Text fontSize="13px" color="white">
            {rarity && rarity(t)}
          </Text>
        </Grid>
        <Grid gridTemplateColumns="1fr 1fr" mt={4}>
          <Box>
            <Text fontSize="13px" color="white">
              {t('Health Meter')}
            </Text>
            <Flex gap="8px" alignItems="center" mt={2}>
              <HealthIconImage health={mole?.health} size="24px" />
              <Text fontSize="20px" bold color="white">
                {mole?.health ?? 0}
              </Text>
            </Flex>
          </Box>
          <Box>
            <Text fontSize="13px" color="white">
              {t('Farm Staking Status')}
            </Text>
            <Flex gap="8px" alignItems="center" mt={2} mb={3}>
              <PixelatedImage
                src={mole?.isStaked ? '/images/vvsGotchi/staked.png' : '/images/vvsGotchi/not_staked.png'}
                whenRetina
                width="24px"
              />
              <Text fontSize="20px" bold color="white">
                {mole?.isStaked ? t('Staked') : t('Not staked')}
              </Text>
            </Flex>
            {isOwning && (
              <Link href={Path.VVS_FARM} target="_blank">
                {t('Farm Page')}
              </Link>
            )}
          </Box>
        </Grid>
      </StyledTeamMoleProfileWrapper>
      <StyledTeamEditContainer>
        <Box p="16px 15px 32px 15px">
          <Grid gridTemplateColumns="1fr 1fr" mt={4} mb={5}>
            <Box>
              <Text fontSize="13px" color="white">
                {t('EXP Earned')}
              </Text>
              <Flex gap="8px" alignItems="center" mt={2}>
                <Img src="/images/vvsGotchi/star.png" width="23px" />
                <Text fontSize="20px" bold color="white">
                  {formatNumber(mole?.totalExp ?? 0, 1, 3)}
                </Text>
              </Flex>
            </Box>
            <Box>
              <Text fontSize="13px" color="white">
                {t('Tier')}
              </Text>
              <Flex gap="8px" alignItems="center" my={2}>
                {moleTierIconSrc && <PixelatedImage src={moleTierIconSrc} width={tierToIconSize[mole.tier].md} />}
                <Text fontSize="20px" bold color="white">
                  {tierName?.(t).full}
                </Text>
              </Flex>
              <Text bold color="white">
                {mole?.boost
                  ? t('%nftBoostMultiplier%x NFT Boost Multiplier', {
                      nftBoostMultiplier: mole.boost,
                    })
                  : '-'}
              </Text>
            </Box>
          </Grid>

          <MoleProgressBar
            my={2}
            rankPercent={mole?.hasRankTier ? tierRankPercentage(mole.rank, mole.tier, rarityTierDetails[mole.tier]) : 2}
          >
            <Text color="black" bold ml={2}>
              {mole?.hasRankTier ? withRankSuffix(mole.rank) : '-'}
            </Text>
          </MoleProgressBar>

          <Flex justifyContent="space-between" mt={4}>
            {mole &&
              Object.keys(Tier).map((e) => {
                const tierIconSrc = tierToIcon[e]
                return (
                  tierIconSrc && (
                    <Flex key={e} flexDirection="column" alignItems="center" justifyContent="end">
                      <PixelatedImage src={tierIconSrc} width={tierToIconSize[e].md} mb={2} />
                      <Text fontSize="11px" color="white">
                        {tierToName[e]?.(t).short}
                      </Text>
                      <Text color="white">{rarityTierDetails[e].boost}x</Text>
                    </Flex>
                  )
                )
              })}
            <Flex flexDirection="column" alignItems="center" justifyContent="end">
              <PixelatedImage src="/images/vvsGotchi/crown.png" width="32px" mb={2} />
              <Text fontSize="11px" color="white">
                1st
              </Text>
              {mole && <Text color="white">{rarityTierMatrix[mole.rarity][Tier.GOD].boost}x</Text>}
            </Flex>
          </Flex>
        </Box>
        <ScreenFooter background={theme.colors.darkestNightBlue}>
          {isInOwnerTeam ? (
            <RemoveFromTeamBtn mole={mole} />
          ) : (
            <ActionButton
              onClick={() =>
                window.open(
                  getVVSMinerMoleMintedUrl(mole.tokenId),
                  isMobile() ? '_self' : '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              {t('View on Minted')}
            </ActionButton>
          )}
        </ScreenFooter>
      </StyledTeamEditContainer>
    </DarkPageContainer>
  )
}

export default MoleProfile

const TIER_PERCENTAGES: Record<Tier, [base: number, range: number]> = {
  [Tier.GOD]: [0.61, 0.38],
  [Tier.MASTER]: [0.2, 0.38],
  [Tier.JUNIOR]: [0, 0.19],
}
const tierRankPercentage = (rank: number, tier: Tier, rarityTierDetail: RarityTierDetails) => {
  const [tierBase, tierRange] = TIER_PERCENTAGES[tier]
  if (tier === Tier.GOD && rank === 1) return 0
  const rankPercentage =
    tierBase +
    ((rarityTierDetail.rangeEnd - rank) / (rarityTierDetail.rangeEnd - rarityTierDetail.rangeStart)) * tierRange
  return Math.min(Math.max(1 - rankPercentage, 0), 1)
}
